'use client'

import React, { useLayoutEffect, useRef, useState } from 'react'

import ReactDOM from 'react-dom'

import { Box } from '@chakra-ui/react'

import {
  ColorScheme,
  TooltipPlacement,
  TooltipVariant,
  tooltipStyles,
} from './Tooltip.styles'
import { clampTooltipPos } from './Tooltip.utils'

export type { TooltipPlacement, TooltipVariant }

export type TooltipProps = {
  label: React.ReactNode
  anchorEl: HTMLElement | null
  colorScheme?: ColorScheme
  placement?: TooltipPlacement
  variant?: TooltipVariant
  /**
   * Lets the tooltip receive pointer events so the operator can move onto it
   * and interact with something inside — e.g. a copy button. Off by default:
   * a passive hint must not intercept clicks aimed at the content beneath it.
   * Pair it with `onMouseEnter`/`onMouseLeave` to keep the tooltip open while
   * the pointer travels from the anchor onto the tooltip.
   */
  isInteractive?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

const VIEWPORT_PADDING = 8

const calcPos = (
  rect: DOMRect,
  placement: TooltipPlacement
): { top: number; left: number } => {
  switch (placement) {
    case 'right':
      return { top: rect.top + rect.height / 2, left: rect.right + 6 }
    case 'bottom':
      return { top: rect.bottom + 6, left: rect.left + rect.width / 2 }
    case 'left':
      // The transform `translateX(calc(-100% - 6px))` already offsets by the
      // tooltip width + gap, so pos.left should be the anchor's left edge only.
      return { top: rect.top + rect.height / 2, left: rect.left }
    default:
      return { top: rect.top, left: rect.left + rect.width / 2 }
  }
}

export const Tooltip = ({
  label,
  anchorEl,
  colorScheme = 'dark',
  placement = 'top',
  variant = 'label',
  isInteractive = false,
  onMouseEnter,
  onMouseLeave,
}: TooltipProps) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const rafRef = useRef<number | null>(null)
  const tooltipRef = useRef<HTMLElement | null>(null)

  useLayoutEffect(() => {
    if (!anchorEl) {
      setPos(null)
      setIsVisible(false)
      return
    }

    const update = () => {
      if (rafRef.current !== null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        const rect = anchorEl.getBoundingClientRect()
        setPos(calcPos(rect, placement))
        setIsVisible(false)
      })
    }

    update()

    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)

    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [anchorEl, placement])

  // Second pass: once the tooltip is rendered (hidden), measure and clamp to
  // keep it fully within the viewport before making it visible.
  useLayoutEffect(() => {
    if (!pos || isVisible || !tooltipRef.current) return

    const clamped = clampTooltipPos(
      pos,
      tooltipRef.current,
      placement,
      VIEWPORT_PADDING
    )
    if (clamped.left !== pos.left || clamped.top !== pos.top) {
      setPos(clamped)
    }

    setIsVisible(true)
  }, [pos, isVisible, placement])

  if (!pos || typeof document === 'undefined') return null

  return ReactDOM.createPortal(
    <Box
      as='span'
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={tooltipRef}
      visibility={isVisible ? 'visible' : 'hidden'}
      {...tooltipStyles(
        pos.top,
        pos.left,
        colorScheme,
        placement,
        variant,
        isInteractive
      )}
    >
      {label}
    </Box>,
    document.body
  )
}
